
import { Mail, MapPin, Calendar } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import federicoPhoto from "@/assets/federico.jpg";
import marcoPhoto from "@/assets/marco.jpg";

const contactInfo = {
  email: "contact@kepleriontech.com",
  location: "Montevideo, Uruguay",
  coFounders: [
    {
      name: "Federico Marioni",
      image: federicoPhoto
    },
    {
      name: "Marco Marioni",
      image: marcoPhoto
    }
  ]
};

// Classes are listed as literals so Tailwind picks them up at build time.
const HIGHLIGHT_CLASSES = ['ring-2', 'ring-primary', 'ring-offset-2', 'shadow-xl'];

const highlightContactForm = () => {
  const form = document.getElementById('contact-form');
  if (!form) return;

  form.scrollIntoView({ behavior: 'smooth', block: 'center' });
  form.classList.add(...HIGHLIGHT_CLASSES);

  const firstField = form.querySelector<HTMLInputElement>('input[name="name"]');
  // Delay focus so it doesn't fight the smooth scroll
  window.setTimeout(() => firstField?.focus({ preventScroll: true }), 500);

  window.setTimeout(() => form.classList.remove(...HIGHLIGHT_CLASSES), 2500);
};

const ContactInfo = ({ isVisible = true }) => {
  return (
    <div 
      className={cn(
        "transition-all duration-700 delay-300",
        isVisible ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-10"
      )}
    >
      <h3 className="text-2xl font-medium mb-6">Let's Build Something Amazing Together</h3>
      <p className="text-muted-foreground mb-8">
        Whether you have a question about our services, want to discuss a project, or just want to say hello, we're here to help.
      </p>
      
      <div className="grid gap-6 mb-10">
        <div className="flex items-center">
          <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center text-primary mr-4">
            <Mail size={22} />
          </div>
          <div>
            <h4 className="font-medium">Email</h4>
            <a href={`mailto:${contactInfo.email}`} className="text-muted-foreground hover:text-primary transition-colors">
              {contactInfo.email}
            </a>
          </div>
        </div>
        
        <div className="flex items-center">
          <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center text-primary mr-4">
            <MapPin size={22} />
          </div>
          <div>
            <h4 className="font-medium">Location</h4>
            <p className="text-muted-foreground">{contactInfo.location}</p>
          </div>
        </div>
      </div>
      
      <div className="mt-10">
        <h4 className="text-xl font-medium mb-4">Book a Call With Our Co-Founders</h4>
        <div className="grid gap-4">
          {contactInfo.coFounders.map((founder, index) => (
            <button 
              key={index}
              type="button"
              onClick={highlightContactForm}
              className="flex items-center w-full text-left p-4 border border-border rounded-lg hover:border-primary/50 transition-colors"
            >
              <Avatar className="w-16 h-16 mr-4 border border-primary/20">
                <AvatarImage src={founder.image} alt={founder.name} />
                <AvatarFallback>{founder.name.charAt(0)}</AvatarFallback>
              </Avatar>
              <div className="flex-1">
                <h5 className="font-medium">{founder.name}</h5>
                <p className="text-sm text-muted-foreground">Send us a message to set up a call</p>
              </div>
              <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-primary ml-2">
                <Calendar size={16} />
              </div>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

export default ContactInfo;
